import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function NewAbout() {
    const aboutRef = useRef<HTMLDivElement | null>(null);
    const castRef = useRef<HTMLDivElement | null>(null);
    const barRef = useRef<HTMLDivElement | null>(null);

    const productsRef = useRef<HTMLDivElement | null>(null);
    const clientsRef = useRef<HTMLDivElement | null>(null);
    const countriesRef = useRef<HTMLDivElement | null>(null);

    const [products, setProducts] = useState<number>(0);
    const [clients, setClients] = useState<number>(0);
    const [countries, setCountries] = useState<number>(0);

    useEffect(() => {
        const aboutLetters = gsap.utils.toArray<HTMLElement>('.about-letter');
        const castLetters = gsap.utils.toArray<HTMLElement>('.cast-letter');

        gsap.fromTo(
            aboutLetters,
            { color: 'rgba(220, 220, 220, 0.2)' },
            {
                color: 'black',
                stagger: 0.15,
                duration: 1,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: aboutRef.current,
                    start: 'top 80%',
                    toggleActions: 'play none none reverse',
                },
            }
        );

        gsap.fromTo(
            castLetters,
            { color: 'rgba(220, 220, 220, 0.2)' },
            {
                color: 'black',
                stagger: 0.15,
                duration: 1,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: castRef.current,
                    start: 'top 80%',
                    toggleActions: 'play none none reverse',
                },
            }
        );

        gsap.fromTo(
            barRef.current,
            { backgroundColor: 'rgba(220, 220, 220, 0.2)' },
            {
                backgroundColor: 'black',
                duration: 1.5,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: castRef.current,
                    start: 'top 80%',
                    toggleActions: 'play none none reverse',
                },
            }
        );

        // Function to animate numbers
        const animateNumbers = (
            target: HTMLDivElement | null,
            end: number,
            setFunction: React.Dispatch<React.SetStateAction<number>>,
            step: number
        ) => {
            if (!target) return;

            let startValue = 0;
            const duration = 2; // Animation duration in seconds
            const intervalTime = (duration * 1000) / (end / step); // Interval time for smooth counting
            let interval: NodeJS.Timeout | null = null;

            ScrollTrigger.create({
                trigger: target,
                start: 'top 80%',
                toggleActions: 'play none none reverse',
                onEnter: () => {
                    startValue = 0;
                    setFunction(0);
                    interval = setInterval(() => {
                        startValue += step;
                        if (startValue >= end) {
                            setFunction(end);
                            if (interval) clearInterval(interval);
                        } else {
                            setFunction(startValue);
                        }
                    }, intervalTime);
                },
                onLeaveBack: () => {
                    setFunction(0);
                    if (interval) clearInterval(interval);
                }
            });
        };

        animateNumbers(productsRef.current, 700, setProducts, 10);
        animateNumbers(clientsRef.current, 3000, setClients, 100);
        animateNumbers(countriesRef.current, 6, setCountries, 1);

    }, []);

    return (
        <div id='about' className='w-full flex md:gap-3 font-sfui flex-col md:flex-row'>
            <div className='w-full md:w-1/2 h-full flex-col-reverse'>
                <div className='flex md:gap-2 md:ps-5'>
                    <div className='flex justify-center items-end  w-[60%] px-3 font-sfui font-light'>
                        The company is always equipped with up-to-date international standards in the industry, knowing the swift advancement of every item technologies. It offers flexible, innovative, modern solutions in a cost-effective way to the emerging market, edified with modern practice.
                    </div>
                    <div ref={productsRef} className='w-[40%] h-[320px] relative'>
                        <img src="/assets/server_11.png" alt="" className='h-full bg-contain w-full' />
                        <div className='bg-[#eed5a7b7] rounded-l-sm absolute w-32 h-28 z-30 right-0 bottom-5 flex justify-center items-center flex-col text-white'>
                            <h1 className='text-4xl font-bold font-sfui'>{products}+</h1>
                            <p className='font-sfui'>Products</p>
                        </div>
                    </div>
                </div>
                <div className='flex flex-col items-end px-2 md:px-0 py-2 md:py-0'>
                    <h1 ref={aboutRef} className='lg:text-9xl md:text-8xl text-6xl md:tracking-widest tracking-[1.7rem]'>
                        {Array.from('ABOUT').map((letter, index) => (
                            <span key={index} className='about-letter'>{letter}</span>
                        ))}
                    </h1>
                    <div ref={castRef} className='lg:text-9xl md:text-8xl text-6xl md:tracking-widest tracking-[1.7rem] flex justify-center items-center'>
                        <div ref={barRef} className='md:h-2 h-1 w-8 lg:w-24 md:w-20 me-5 bg-black'></div>
                        {Array.from('TRIOS').map((letter, index) => (
                            <span key={index} className='cast-letter font-light'>{letter}</span>
                        ))}
                    </div>
                </div>
            </div>
            <div className='md:w-1/2 w-full flex flex-row-reverse md:flex-row space-x-0 md:space-x-3 !ms-0 h-full'>
                <div ref={clientsRef} className='w-1/2 h-full relative'>
                    <img src="/assets/cctv_11.png" alt="" className='h-full w-full' />
                    <div className='bg-[#eed5a7b7] absolute w-32 h-28 z-30 right-0 bottom-5 flex flex-col justify-center items-center text-white rounded-l-sm'>
                        <h1 className='text-4xl font-bold font-sfui'>{clients}+</h1>
                        <p className='font-sfui'>Clients</p>
                    </div>
                </div>
                <div ref={countriesRef} className='w-1/2 max-h-[75%] flex items-end md:items-start relative'>
                    <img src="/assets/printer_11.png" alt="" className='h-[75%] w-full' />
                    <div className='bg-[#eed5a7b7] absolute w-32 h-28 z-30 right-0 md:bottom-40 bottom-5 flex flex-col justify-center items-center text-white rounded-l-sm'>
                        <h1 className='text-4xl font-bold font-sfui'>{countries}+</h1>
                        <p className='font-sfui'>Countries</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
